import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { BookOpen, Trash2, Eye, EyeOff, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminGuestbook, updateGuestbookMessage, deleteGuestbookMessage } from '@/lib/api';

const AdminGuestbook = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, message: null });

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await getAdminGuestbook();
      setMessages(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (msg) => {
    try {
      await updateGuestbookMessage(msg.id, { is_approved: !msg.is_approved });
      toast.success(msg.is_approved ? 'Message masqué' : 'Message approuvé');
      loadMessages();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.message) return;
    try {
      await deleteGuestbookMessage(deleteDialog.message.id);
      toast.success('Message supprimé');
      setDeleteDialog({ open: false, message: null });
      loadMessages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Livre d'Or d'avant mariage">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Livre d'Or d'avant mariage">
      <div className="space-y-6">
        {/* Stats */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-2">
            <span className="text-2xl font-bold text-gray-900">{messages.length}</span>
            <span className="text-sm text-gray-500 ml-2">message{messages.length > 1 ? 's' : ''}</span>
          </div>
          <div className="bg-white rounded-lg border border-green-200 px-4 py-2">
            <span className="text-2xl font-bold text-green-600">{messages.filter(m => m.is_approved).length}</span>
            <span className="text-sm text-gray-500 ml-2">validé{messages.filter(m => m.is_approved).length > 1 ? 's' : ''}</span>
          </div>
          {messages.filter(m => !m.is_approved).length > 0 && (
            <div className="bg-amber-50 rounded-lg border border-amber-300 px-4 py-2 animate-pulse">
              <span className="text-2xl font-bold text-amber-600">{messages.filter(m => !m.is_approved).length}</span>
              <span className="text-sm text-amber-700 ml-2">en attente</span>
            </div>
          )}
        </div>

        {/* Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-terracotta" />
              Messages du livre d'or d'avant mariage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun message pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Show pending messages first */}
                {messages
                  .sort((a, b) => (a.is_approved === b.is_approved ? 0 : a.is_approved ? 1 : -1))
                  .map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg border ${
                      !msg.is_approved 
                        ? 'bg-amber-50 border-amber-300' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-charcoal">{msg.name}</span>
                          {!msg.is_approved ? (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                              ⏳ En attente de validation
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">
                              ✓ Validé
                            </Badge>
                          )}
                        </div>
                        <p className="text-stone text-sm italic">"{msg.message}"</p>
                        <p className="text-xs text-gray-400 mt-2">{formatDate(msg.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={msg.is_approved ? "ghost" : "default"}
                          size="sm"
                          onClick={() => toggleApproval(msg)}
                          title={msg.is_approved ? 'Masquer' : 'Valider'}
                          className={!msg.is_approved ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                        >
                          {msg.is_approved ? (
                            <EyeOff className="w-4 h-4 text-gray-500" />
                          ) : (
                            <><Eye className="w-4 h-4 mr-1" /> Valider</>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, message: msg })}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, message: deleteDialog.message })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer ce message ?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer le message de <strong>{deleteDialog.message?.name}</strong> ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, message: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminGuestbook;
